import { IconEye, IconEyeOff } from '@tabler/icons-react-native'
import { forwardRef, useCallback, useId, useMemo, useState } from 'react'
import { TextInput as RNTextInput, type NativeSyntheticEvent, type TextInputFocusEventData } from 'react-native'

import {
  FormInput,
  Input,
  InputField,
  InputIcon,
  InputSlot,
  type FormInputProps,
  type InputFieldProps,
  type InputProps
} from '@/components'

export interface PasswordInputProps extends Omit<FormInputProps, 'children'> {
  inputProps?: InputProps
  inputFieldProps?: Omit<InputFieldProps, 'onChange' | 'onChangeText' | 'value'>
  onBlur?: (event: NativeSyntheticEvent<TextInputFocusEventData>) => void
  onChange: (value: string) => void
  value?: string | null | undefined
  accessibilityLabel?: string
}

export const PasswordInput = forwardRef<RNTextInput, PasswordInputProps>(
  (
    {
      accessibilityLabel: _accessibilityLabel,
      error,
      helper,
      isDisabled,
      isRequired,
      inputProps,
      inputFieldProps,
      label = 'Password',
      onBlur,
      onChange,
      value,
      ...props
    },
    ref
  ) => {
    const nativeId = useId()
    const [secureTextEntry, setSecureTextEntry] = useState(true)
    const accessibilityLabel = useMemo(
      () => _accessibilityLabel ?? `${label}${isRequired ? '. Required' : ''}${helper ? `. ${helper}` : ''}`,
      [_accessibilityLabel, label, isRequired, helper]
    )

    return (
      <FormInput
        error={error}
        helper={helper}
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        nativeID={nativeId}
        {...props}
      >
        <Input {...inputProps}>
          <InputField
            accessibilityLabel={accessibilityLabel}
            aria-disabled={isDisabled}
            aria-errormessage={error ? `${nativeId}-error` : undefined}
            aria-invalid={!!error}
            aria-label={accessibilityLabel} // needed because gluestack is stupid
            aria-required={isRequired}
            autoCapitalize='none'
            autoCorrect={false}
            className='bg-white font-poppinsMedium color-black'
            onBlur={onBlur}
            onChangeText={onChange}
            passwordRules='minlength: 8; required: lower; required: upper; required: digit; required: [!#$%&()*+,./:;<=>?@^_|~];'
            // @ts-expect-error because gluestack types are stupid
            ref={ref}
            secureTextEntry={secureTextEntry}
            value={value ?? ''}
            {...inputFieldProps}
          />
          <InputSlot
            accessibilityElementsHidden
            aria-hidden
            className='h-full w-10 items-center justify-center bg-white'
            importantForAccessibility='no-hide-descendants'
            onPress={useCallback(() => setSecureTextEntry((prev) => !prev), [])}
          >
            <InputIcon as={secureTextEntry ? IconEyeOff : IconEye} />
          </InputSlot>
        </Input>
      </FormInput>
    )
  }
)

PasswordInput.displayName = 'PasswordInput'
